import { ref, computed } from 'vue'

// Global state for enhancement progress
const isEnhancing = ref(false)
const progress = ref(0)
const analysisType = ref('template') // 'template' or 'image'
const currentPhase = ref('') // 'analyze' or 'generate'
let progressInterval = null
let phaseStartTime = null
const TOTAL_DURATION = 10000 // 10 seconds in milliseconds

export function useEnhancementProgress() {
  
  // Computed property to check if modal should be visible
  const isModalVisible = computed(() => isEnhancing.value)
  
  // Computed property to return rounded progress value
  const roundedProgress = computed(() => Math.round(progress.value))
  
  // Start enhancement with animated progress
  const startEnhancement = (type = 'template', phase = 'analyze') => {
    console.log(`🎨 Starting enhancement progress tracking for: ${type}, phase: ${phase}`)
    
    // If already enhancing, don't restart - just update phase if needed
    if (isEnhancing.value) {
      console.log('⚠️ Enhancement already active, updating phase only')
      currentPhase.value = phase
      return
    }
    
    isEnhancing.value = true
    progress.value = 0
    analysisType.value = type
    currentPhase.value = phase
    phaseStartTime = Date.now()
    
    // Clear any existing interval
    if (progressInterval) {
      clearInterval(progressInterval)
    }
    
    // Animate progress based on elapsed time
    progressInterval = setInterval(() => {
      const elapsed = Date.now() - phaseStartTime
      const progressPercent = Math.min(95, (elapsed / TOTAL_DURATION) * 100)
      progress.value = Math.round(progressPercent)
      
      // Auto-complete if we reach the end
      if (elapsed >= TOTAL_DURATION) {
        clearInterval(progressInterval)
        progressInterval = null
        progress.value = 100
        
        // Auto-hide after showing completion
        setTimeout(() => {
          isEnhancing.value = false
          progress.value = 0
        }, 800)
      }
    }, 100) // Update every 100ms for smooth progress
  }
  
  // Set phase (for transitioning between analyze and generate)
  const setPhase = (phase) => {
    console.log(`🔄 Transitioning to phase: ${phase}`)
    currentPhase.value = phase
    // Don't reset progress when transitioning phases
  }
  
  // Complete enhancement (set to 100% and hide modal)
  const completeEnhancement = () => {
    console.log('✅ Enhancement completed, finishing progress...')
    
    // Clear interval
    if (progressInterval) {
      clearInterval(progressInterval)
      progressInterval = null
    }
    
    // Set to 100% briefly before hiding
    progress.value = 100
    
    // Hide modal after a short delay to show completion
    setTimeout(() => {
      isEnhancing.value = false
      progress.value = 0
      currentPhase.value = ''
      phaseStartTime = null
    }, 800)
  }
  
  // Cancel enhancement (immediate hide)
  const cancelEnhancement = () => {
    console.log('❌ Enhancement cancelled')
    
    // Clear interval
    if (progressInterval) {
      clearInterval(progressInterval)
      progressInterval = null
    }
    
    // Reset state
    isEnhancing.value = false
    progress.value = 0
    analysisType.value = 'template'
    currentPhase.value = ''
    phaseStartTime = null
  }
  
  // Set specific progress value (for manual control)
  const setProgress = (value) => {
    progress.value = Math.max(0, Math.min(100, Math.round(value)))
  }
  
  // Update progress with specific milestones (deprecated - progress is now time-based)
  const updateProgressMilestone = (milestone) => {
    console.log(`🎯 Progress milestone: ${milestone} (time-based progress active)`)
    // Milestones are now just for logging/tracking - progress is controlled by timer
  }
  
    return {
    // State
    isEnhancing: computed(() => isEnhancing.value),
    isModalVisible,
    progress: roundedProgress,
    analysisType: computed(() => analysisType.value),
    currentPhase: computed(() => currentPhase.value),
    
    // Methods
    startEnhancement,
    completeEnhancement,
    cancelEnhancement,
    setProgress,
    updateProgressMilestone,
    setPhase
  }
}

// Export for global access
export default useEnhancementProgress
